import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

const WishlistContext = createContext();

const STORAGE_KEY = 'apollo_shades_wishlist';
const RECENTLY_VIEWED_KEY = 'apollo_shades_recently_viewed';
const MAX_RECENTLY_VIEWED = 10;

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  const hydratedRef = useRef(false);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const savedWishlist = localStorage.getItem(STORAGE_KEY);
      if (savedWishlist) {
        setWishlist(JSON.parse(savedWishlist));
      }
      
      const savedRecent = localStorage.getItem(RECENTLY_VIEWED_KEY);
      if (savedRecent) {
        setRecentlyViewed(JSON.parse(savedRecent));
      }
    } catch (e) {
      console.error('Failed to load from localStorage:', e);
    }
    hydratedRef.current = true;
    setHydrated(true);
  }, []);

  // Save wishlist to localStorage
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(wishlist));
    } catch (e) {
      console.error('Failed to save wishlist:', e);
    }
  }, [wishlist, hydrated]);

  // Save recently viewed to localStorage
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(recentlyViewed));
    } catch (e) {
      console.error('Failed to save recently viewed:', e);
    }
  }, [recentlyViewed, hydrated]);

  const addToWishlist = useCallback((product) => {
    setWishlist(prev => {
      if (prev.some(p => p.product_type === product.product_type || p.id === product.id)) {
        return prev; // Already in wishlist
      }
      toast.success(`${product.display_name || product.title} added to wishlist!`);
      return [...prev, {
        id: product.id,
        product_type: product.product_type,
        title: product.display_name || product.title,
        description: product.short_description || product.description?.slice(0, 100),
        image: product.images?.[0]?.url || product.image,
        price_from: product.price_from || product.base_price,
        category: product.category_slug,
        added_at: new Date().toISOString()
      }];
    });
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    setWishlist(prev => {
      const product = prev.find(p => p.product_type === productId || p.id === productId);
      if (product) {
        toast.success(`${product.title} removed from wishlist`);
      }
      return prev.filter(p => p.product_type !== productId && p.id !== productId);
    });
  }, []);

  const isInWishlist = useCallback((productId) => {
    return wishlist.some(p => p.product_type === productId || p.id === productId);
  }, [wishlist]);

  const toggleWishlist = useCallback((product) => {
    const id = product.product_type || product.id;
    const inWishlist = wishlist.some(p => p.product_type === id || p.id === id);
    if (inWishlist) {
      removeFromWishlist(id);
    } else {
      addToWishlist(product);
    }
  }, [wishlist, addToWishlist, removeFromWishlist]);

  const clearWishlist = useCallback(() => {
    setWishlist([]);
    toast.success('Wishlist cleared');
  }, []);

  // Recently viewed
  const addToRecentlyViewed = useCallback((product) => {
    // Defer until hydrated to avoid overwriting LS-loaded data
    const doAdd = () => setRecentlyViewed(prev => {
      // Remove if already exists (to move to front)
      const filtered = prev.filter(p => 
        p.product_type !== product.product_type && p.id !== product.id
      );
      
      // Add to front
      const updated = [{
        id: product.id,
        product_type: product.product_type,
        title: product.display_name || product.title,
        description: product.short_description || product.description?.slice(0, 100),
        image: product.images?.[0]?.url || product.image,
        price_from: product.price_from || product.base_price,
        category: product.category_slug,
        viewed_at: new Date().toISOString()
      }, ...filtered];
      
      // Keep only last N
      return updated.slice(0, MAX_RECENTLY_VIEWED);
    });
    if (hydratedRef.current) {
      doAdd();
    } else {
      // Wait for hydration
      setTimeout(doAdd, 100);
    }
  }, []);

  const clearRecentlyViewed = useCallback(() => {
    setRecentlyViewed([]);
  }, []);

  return (
    <WishlistContext.Provider value={{
      wishlist,
      recentlyViewed,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      toggleWishlist,
      clearWishlist,
      addToRecentlyViewed,
      clearRecentlyViewed,
      wishlistCount: wishlist.length
    }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export default WishlistContext;
